/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { UtteranceObservable } from 'src/luis/appShell/buildPane/intentPage/types';
import { LuisId } from 'src/luis/types/Resource';

type ModalsConfigState = {
    isDeleteModalOpen: boolean;
    isEntityModalOpen: boolean;
    isAddToPatternsModalOpen: boolean;
    isEditUtteranceModalOpen: boolean;
};

/**
 * Reactive store for intent page Ops states.
 */
export class IntentPageOpsStore {
    @observable.shallow
    public selectedItems: UtteranceObservable[];

    @observable
    public modalsConfigState: {
        isDeleteModalOpen: boolean;
        isEntityModalOpen: boolean;
        isAddToPatternsModalOpen: boolean;
        isEditUtteranceModalOpen: boolean;
    };

    @observable
    public newEntityCreationName: string;

    @observable
    public selectedUtteranceId: LuisId;

    @observable
    public isUtteranceEditModeOn: boolean;

    @observable
    public searchQuery: string;

    constructor() {
        this.selectedItems = [];
        this.modalsConfigState = {
            isDeleteModalOpen: false,
            isEntityModalOpen: false,
            isAddToPatternsModalOpen: false,
            isEditUtteranceModalOpen: false
        };
        this.newEntityCreationName = '';
        this.selectedUtteranceId = null;
        this.isUtteranceEditModeOn = false;
        this.searchQuery = '';
    }

    /**
     * Setter for the edit utterance mode flag.
     */
    @action
    public setIsUtteranceEditModeOn(isEditModeOn: boolean) {
        this.isUtteranceEditModeOn = isEditModeOn;
    }

    /**
     * Setter for the selected utterance id used for the inline options operations.
     */
    @action
    public setSelectedUtteranceId(utteranceId: LuisId) {
        this.selectedUtteranceId = utteranceId;
    }

    /**
     * Setter for the entity name to be created from the entity dropdown.
     */
    @action
    public setNewEntityCreationName(entityName: string) {
        this.newEntityCreationName = entityName;
    }

    /**
     * Setter for the modals hidden states.
     */
    @action
    public setModalsConfigState(configs: Partial<ModalsConfigState>) {
        this.modalsConfigState = { ...this.modalsConfigState, ...configs };
    }

    /**
     * Reset the modals hidden states to the default state.
     */
    @action
    public resetModalsConfigs() {
        this.modalsConfigState = {
            isDeleteModalOpen: false,
            isEntityModalOpen: false,
            isAddToPatternsModalOpen: false,
            isEditUtteranceModalOpen: false
        };
    }
}
